import React from 'react';
import { BookOpen, User, MapPin, Star, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import RequestActionsMenu from './RequestActionsMenu';
import { isOverdue, formatOverdueInfo, formatRentalDate } from '../utils/dateUtils';

interface BookRequest {
  id: string;
  book_id: string;
  title: string;
  author: string;
  cover_image: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'completed';
  created_at: string;
  owner_name?: string;
  owner_city?: string;
  requester_name?: string;
  requester_city?: string;
  requester_average_rating?: number;
  requester_total_ratings?: number;
  requester_trust_score?: number;
  can_rate?: boolean | number;
  rental_start_date?: string;
  rental_end_date?: string;
}

interface RequestsTableProps {
  requests: BookRequest[];
  activeTab: 'sent' | 'received';
  onSort: (column: string) => void;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  onActionComplete: () => void;
  onRequestClick?: (request: BookRequest) => void;
  onOpenRatingModal?: (request: BookRequest) => void;
  onOpenCancelModal?: (request: BookRequest) => void;
}

const RequestsTable: React.FC<RequestsTableProps> = ({
  requests,
  activeTab,
  onSort,
  sortColumn,
  sortDirection,
  onActionComplete,
  onRequestClick,
  onOpenRatingModal,
  onOpenCancelModal
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatOwnerName = (fullName: string) => {
    if (!fullName) return '';
    const nameParts = fullName.trim().split(' ');
    if (nameParts.length === 1) {
      return nameParts[0];
    }
    const firstName = nameParts[0];
    const lastNameInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastNameInitial}.`;
  };

  const getStatusBadge = (status: string, request: BookRequest) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'En attente' },
      accepted: { color: 'bg-green-100 text-green-800', text: 'Acceptée' },
      rejected: { color: 'bg-red-100 text-red-800', text: 'Refusée' },
      cancelled: { color: 'bg-gray-100 text-gray-800', text: 'Annulée' },
      completed: { color: 'bg-forest-100 text-forest-800', text: 'Terminée' }
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return null;

    return (
      <div className="flex items-center space-x-2">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
          {config.text}
        </span>
        
        {/* Badge "En retard" pour les demandes acceptées avec date dépassée */}
        {status === 'accepted' && request.rental_end_date && isOverdue(request.rental_end_date) && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            <AlertTriangle className="h-3 w-3 mr-1" />
            En retard
          </span>
        )}
      </div>
    );
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-forest-700" />
      : <ArrowDown className="h-4 w-4 text-forest-700" />;
  };

  const handleHeaderClick = (column: string) => {
    onSort(column);
  };

  if (requests.length === 0) {
    return (
      <div className="text-center py-12">
        <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Aucune demande trouvée
        </h3>
        <p className="text-gray-500">
          {activeTab === 'sent' 
            ? 'Vous n\'avez pas encore fait de demandes.' 
            : 'Vous n\'avez pas encore reçu de demandes.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-visible pb-8">
      {/* Tableau Desktop */}
      <div className="hidden lg:block">
        <table className="w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="w-1/4 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleHeaderClick('book')}
              >
                <div className="flex items-center space-x-1">
                  <span>Livre</span>
                  {getSortIcon('book')}
                </div>
              </th>
              <th 
                className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleHeaderClick('user')}
              >
                <div className="flex items-center space-x-1">
                  <span>Utilisateur</span>
                  {getSortIcon('user')}
                </div>
              </th>
              <th 
                className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleHeaderClick('status')}
              >
                <div className="flex items-center space-x-1">
                  <span>Statut</span>
                  {getSortIcon('status')}
                </div>
              </th>
              <th 
                className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleHeaderClick('date')}
              >
                <div className="flex items-center space-x-1">
                  <span>Date demande</span>
                  {getSortIcon('date')}
                </div>
              </th>
              <th 
                className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleHeaderClick('return')}
              >
                <div className="flex items-center space-x-1">
                  <span>Date retour</span>
                  {getSortIcon('return')}
                </div>
              </th>
              <th className="w-16 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requests.map((request) => (
              <tr 
                key={request.id} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onRequestClick?.(request)}
              >
                {/* Livre */}
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-12 w-8">
                      {request.cover_image ? (
                        <img
                          className="h-12 w-8 object-cover rounded"
                          src={request.cover_image}
                          alt={request.title}
                        />
                      ) : (
                        <div className="h-12 w-8 bg-gray-200 rounded flex items-center justify-center">
                          <BookOpen className="h-4 w-4 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {request.title}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {request.author}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Utilisateur */}
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-forest-100 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-forest-700" />
                      </div>
                    </div>
                    <div className="ml-2 min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {formatOwnerName(activeTab === 'sent' ? request.owner_name || '' : request.requester_name || '')}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {activeTab === 'sent' ? request.owner_city : request.requester_city}
                      </div>
                      {activeTab === 'received' && request.requester_average_rating && request.requester_average_rating > 0 && (
                        <div className="flex items-center text-xs text-yellow-600">
                          <Star className="h-3 w-3 mr-1" />
                          {request.requester_average_rating.toFixed(1)}/5
                        </div>
                      )}
                    </div>
                  </div>
                </td>

                {/* Statut */}
                <td className="px-4 py-4">
                  <div className="flex flex-wrap gap-1">
                    {getStatusBadge(request.status, request)}
                  </div>
                </td>

                {/* Date demande */}
                <td className="px-4 py-4 text-sm text-gray-900">
                  {formatDate(request.created_at)}
                </td>

                {/* Date retour */}
                <td className="px-4 py-4 text-sm text-gray-900">
                  {request.rental_end_date && (request.status === 'accepted' || request.status === 'completed') ? (
                    <div>
                      <div className="text-gray-900">
                        {formatRentalDate(request.rental_end_date)}
                      </div>
                      {isOverdue(request.rental_end_date) && (
                        <div className="text-xs text-orange-600 mt-1">
                          {formatOverdueInfo(request.rental_end_date)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>

                {/* Actions */}
                <td className="px-4 py-4 text-center">
                  <RequestActionsMenu
                    request={request}
                    activeTab={activeTab}
                    onActionComplete={onActionComplete}
                    onOpenRatingModal={onOpenRatingModal}
                    onOpenCancelModal={onOpenCancelModal}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards Mobile */}
      <div className="lg:hidden">
        {requests.map((request) => (
          <div key={request.id} className="border-b border-gray-200 last:border-b-0">
            <div className="p-4 hover:bg-gray-50">
              <div className="flex items-start space-x-3">
                {/* Image du livre */}
                <div className="flex-shrink-0">
                  {request.cover_image ? (
                    <img
                      className="h-16 w-12 object-cover rounded"
                      src={request.cover_image}
                      alt={request.title}
                    />
                  ) : (
                    <div className="h-16 w-12 bg-gray-200 rounded flex items-center justify-center">
                      <BookOpen className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 line-clamp-2">
                        {request.title}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-1">
                        {request.author}
                      </p>
                      
                      {/* Utilisateur */}
                      <div className="mt-2 flex items-center text-sm text-gray-600">
                        <User className="h-4 w-4 mr-1 flex-shrink-0" />
                        <span className="truncate">
                          {formatOwnerName(activeTab === 'sent' ? request.owner_name || '' : request.requester_name || '')}
                        </span>
                        {activeTab === 'sent' ? request.owner_city && (
                          <>
                            <MapPin className="h-4 w-4 ml-2 mr-1 flex-shrink-0" />
                            <span className="truncate">{request.owner_city}</span>
                          </>
                        ) : request.requester_city && (
                          <>
                            <MapPin className="h-4 w-4 ml-2 mr-1 flex-shrink-0" />
                            <span className="truncate">{request.requester_city}</span>
                          </>
                        )}
                      </div>

                      {/* Note utilisateur */}
                      {activeTab === 'received' && request.requester_average_rating && request.requester_average_rating > 0 && (
                        <div className="flex items-center text-xs text-yellow-600 mt-1">
                          <Star className="h-3 w-3 mr-1" />
                          {request.requester_average_rating.toFixed(1)}/5
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0 ml-2">
                      <RequestActionsMenu
                        request={request}
                        activeTab={activeTab}
                        onActionComplete={onActionComplete}
                        onOpenRatingModal={onOpenRatingModal}
                        onOpenCancelModal={onOpenCancelModal}
                      />
                    </div>
                  </div>

                  {/* Statut et dates */}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(request.status, request)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDate(request.created_at)}
                    </div>
                  </div>

                  {/* Date retour */}
                  {request.rental_end_date && (request.status === 'accepted' || request.status === 'completed') && (
                    <div className="mt-2 text-xs text-gray-600">
                      Retour prévu: {formatRentalDate(request.rental_end_date)}
                      {isOverdue(request.rental_end_date) && (
                        <div className="text-orange-600 mt-1">
                          {formatOverdueInfo(request.rental_end_date)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RequestsTable;
